import {
  AfterViewInit,
  Component,
  ElementRef,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FullscreenService } from '../../../../core/services/fullscreen.service';
import { ConsultationsService } from '../../../../core/services/consultation/consultations.service';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  FormControl,
} from '@angular/forms';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { finalize, Subscription } from 'rxjs';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { Router } from '@angular/router';
import { GenderUtils } from '../../../../utils/gender-utils';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { environment } from '../../../../../environments/environment';
import { ExpertListModel } from '../../../../core/models/patient/expert-list.model';
import {
  Complaint,
  Medication,
  OtherSymptom,
  Predisposition,
} from '../../../../core/models/patient/medical-informations.model';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { SelectDropDownModule } from 'ngx-select-dropdown';
import { InputBlurDirective } from '../../../directives/input-blur.directive';
import { SkinTypeModel } from '../../../../core/models/patient/skin-type.model';
import { ToastService } from '../../../../core/services/toast.service';
import { dateFormatValidator } from '../../../validators/birth-date-validator.validator';
import { InputValidationService } from '../../../../core/services/input-validation.service';
import { FormatBirthDateService } from '../../../../core/services/format-birth-date.service';
@Component({
  selector: 'app-aps',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FullscreenOverlayComponent,
    TranslateModule,
    SelectDropDownModule,
    InputBlurDirective,
  ],
  templateUrl: './aps.component.html',
  styleUrl: './aps.component.scss',
})
export class ApsComponent implements AfterViewInit {
  @ViewChild('waitingModalModal') waitingModalModal!: ElementRef;
  @ViewChild('nameLastnameBirthDateValidationErrorModal')
  nameLastnameBirthDateValidationErrorModal!: ElementRef;
  @ViewChild('viewer') viewerElement!: ElementRef;
  private backdropElement: HTMLElement | null = null;
  private subscriptions: Subscription = new Subscription();
  patientInformationForm: FormGroup;
  lesionCharacteristicsForm: FormGroup;
  genders = GenderUtils?.getAllGenders();
  dermatologists: ExpertListModel[] = [];
  predispositions: Predisposition[] = [];
  complaints: Complaint[] = [];
  medications: Medication[] = [];
  otherSymptoms: OtherSymptom[] = [];
  isOverlayLoading: boolean = false;
  isBtnEnabled: boolean = true;
  patientImages: any = [];
  selectedImageIndex: number | null = null;
  customerCode: string = '';
  takeImageAgainBtn: boolean = true;
  caseId: string = '';
  patientId: string = '';
  selectedImage: any = null;
  isFormElementsEnabled: boolean = false;
  isSaveAndTakeDisabled: boolean = false;
  maxDate: string;
  currentUser: UserSessionModel | null = null;
  private imageInterval: any;
  patientInformationObject: any = null;
  medicalHistoryArr: any = null;
  selectedRiskFactors: string[] = [];
  selectedDermatologicDiseases: string[] = [];
  selectedNeoplasticDiseases: string[] = [];
  selectedDiseasesCausingImmunosuppression: string[] = [];
  selectedOtherDiseases: string[] = [];
  selectedMedications: string[] = [];
  skinTypes: SkinTypeModel[] = [];
  skinTypeColors: string[] = [
    '#F3D0B0',
    '#E7B48F',
    '#D29E7D',
    '#BA7750',
    '#7A431E',
    '#3C211D',
  ];
  showSaveAndTakePicture: boolean = true;
  isCustomCongenitalChecked: boolean = false;
  showSaveAndTakePictureButton: boolean = true;
  selectedChangeItems: string[] = [];
  showImageGallery: boolean = true;
  fullscreenImage: any = null;
  qrCodeDetails: { url: string };

  constructor(
    private router: Router,
    private fullscreenService: FullscreenService,
    private consultationsService: ConsultationsService,
    private patientsService: PatientsService,
    private currentUserService: CurrentUserService,
    private signalRService: SignalRService,
    private fb: FormBuilder,
    private renderer2: Renderer2,
    private translate: TranslateService,
    private languageService: LanguageService,
    private toastService: ToastService,
    private inputValidationService: InputValidationService,
    private formatBirthDateService: FormatBirthDateService
  ) {
    const userLang = this.languageService.getLanguage() || 'en';
    this.translate.use(userLang);
    this.setSkinTypesDescriptions();
    this.createPatientImageConnection();
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.patientInformationForm = this.fb.group({
      name: ['', [Validators.required, lettersOnlyValidator()]],
      lastName: ['', [Validators.required, lettersOnlyValidator()]],
      birthDate: ['', [Validators.required, dateFormatValidator()]],
      gender: [null, Validators.required],
      height: [null, [Validators.required, Validators.max(300)]],
      weight: [null, [Validators.required, Validators.max(999)]],
      phone1: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(15),
          Validators.pattern('^\\+?[0-9]{7,15}$'),
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      generalAppearances: this.fb.array([]),
      skinType: [null, Validators.required],
    });
    this.lesionCharacteristicsForm = this.fb.group({
      customCongenitalValue: [1, [Validators.min(1), Validators.max(300)]],
      customCongenitalUnit: [this.translate.instant('weeks')],
      complaints: this.fb.array([]),
      since: this.fb.array([]),
      change: this.fb.array([]),
    });
  }

  ngAfterViewInit() {
    this.fullscreenService.initializeFullscreenButtons();
  }

  async ngOnInit() {
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.genders = GenderUtils?.getAllGenders();
        this.setSkinTypesDescriptions();
      })
    );

    await GenderUtils?.initializeTranslateService(this.translate);
    this.genders = GenderUtils?.getAllGenders();
    this.currentUser = this.currentUserService.GetSession();
    this.getMedicalInformations();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.imageInterval) {
      clearInterval(this.imageInterval);
    }
  }

  createPatientRecordWithQrCode() {
    this.patientsService.createPatientRecordWithQrCode(this.caseId).subscribe({
      next: (res) => {
        const blob = new Blob([res], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        this.qrCodeDetails = { url: url };
      },
    });
  }

  setSkinTypesDescriptions() {
    const manipulateSkinTypes: SkinTypeModel[] = [
      {
        id: 1,
        name: this.translate.instant('skinTypes.pale.name'),
        desc: this.translate.instant('skinTypes.pale.desc'),
      },
      {
        id: 2,
        name: this.translate.instant('skinTypes.sensitive.name'),
        desc: this.translate.instant('skinTypes.sensitive.desc'),
      },
      {
        id: 3,
        name: this.translate.instant('skinTypes.fair.name'),
        desc: this.translate.instant('skinTypes.fair.desc'),
      },
      {
        id: 4,
        name: this.translate.instant('skinTypes.olive.name'),
        desc: this.translate.instant('skinTypes.olive.desc'),
      },
      {
        id: 5,
        name: this.translate.instant('skinTypes.dark.name'),
        desc: this.translate.instant('skinTypes.dark.desc'),
      },
      {
        id: 6,
        name: this.translate.instant('skinTypes.veryDark.name'),
        desc: this.translate.instant('skinTypes.veryDark.desc'),
      },
    ];
    this.skinTypes = manipulateSkinTypes;
  }

  protected async createPatientImageConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = 'patient-hub';
    this.signalRService.connect(hubUrl, signalRBasePath);
    this.signalRService.on(hubUrl, 'receiveImageUploaded', signalRBasePath, (message) => {
      const parsedJson = JSON.parse(message);
      if (parsedJson.PatientRecordCaseId === this.caseId) {
        if (parsedJson) {
          const image = {
            createdDate: parsedJson.CreatedDate,
            src: `data:image/jpeg;base64,${parsedJson.FileData}`,
            fileName: parsedJson.FileName,
            id: `imageModal${parsedJson.PatientRecordCaseImageId}`,
            alt: `Lesion Close-up`,
            patientRecordId: parsedJson.PatientRecordCaseImageId,
            isSelected: parsedJson.IsSelected,
          };

          this.patientImages = [image, ...this.patientImages];
          this.isOverlayLoading = false;
        }
      }
    });
  }

  getMedicalInformations() {
    this.consultationsService.medicalInformations().subscribe({
      next: (res) => {
        this.medicalHistoryArr = res;
      },
    });
  }

  getPatientInformationsByEmail(email: string) {
    this.isOverlayLoading = true;
    this.patientsService.patientInformationByEmail(email).subscribe({
      next: (res) => {
        if (res?.isSuccess) {
          this.patientInformationForm.patchValue({
            name: res.name,
            lastName: res.lastName,
            birthDate: res.birthDate ? this.formatDate2(res.birthDate) : null,
            gender: res.gender,
            email: res.email,
            height: res.height,
            weight: res.weight,
            phone1: res.phone1,
          });
        } else {
          this.clearForm();
        }
      },
      error: () => {
        this.isOverlayLoading = false;
      },
      complete: () => {
        this.isOverlayLoading = false;
      },
    });
  }

  isSaveAndTakeImageBtnDiasabled(): boolean {
    return this.patientInformationForm.valid;
  }

  getPatientRecordImagesByPatientId(patientId: string) {
    this.consultationsService
      .patientRecordImagesByPatientId(patientId)
      .subscribe({
        next: (res) => {
          if (res.length > 0) {
            this.patientImages = res.map((image: any, index: number) => ({
              src: `data:image/jpeg;base64,${image.fileData}`,
              id: `imageModal${index + 1}`,
              alt: `Lesion Close-up ${index + 1}`,
              patientRecordId: image.id,
              isSelected: image.isSelected,
            }));
            this.closeWaitingModal();
            this.router.navigate(['/patient/detail', this.patientId]);
          }
        },
      });
  }

  formatDate(dateString: string): string {
    const datePattern = /^\d{2}-\d{2}-\d{4}$/;

    if (!datePattern.test(dateString)) {
      return dateString;
    }

    const [day, month, year] = dateString.split('-').map(Number);

    if (month < 1 || month > 12) {
      return dateString;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return dateString;
    }

    const date = new Date(Date.UTC(year, month - 1, day));

    return date.toISOString().split('T')[0];
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.patientInformationForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  async takePictureAgain() {
    const json = {
      patientRecordCaseId: this.caseId,
    };

    this.patientsService.photoAgain(json).subscribe({
      next: async () => {
        this.toastService.showToast(
          this.translate.instant('successImageProcessDesc'),
          'success'
        );
      },
    });
  }

  selectImage(event: MouseEvent, imag: any) {
    event.preventDefault();

    this.patientImages = this.patientImages.map((image: any) => ({
      ...image,
      isSelected: false,
    }));

    this.patientImages = this.patientImages.map((image: any) =>
      image.id === imag.id ? { ...image, isSelected: true } : image
    );

    this.selectedImage = imag;
    this.patientsService
      .updateSelectedPatientRecordCaseImage(this.selectedImage.patientRecordId)
      .subscribe({
        next: () => {},
      });
  }

  clearForm(): void {
    this.patientInformationForm.patchValue({
      name: '',
      lastName: '',
      birthDate: '',
      gender: null,
    });
  }

  openWaitingModal() {
    if (this.waitingModalModal) {
      this.renderer2.setStyle(
        this.waitingModalModal.nativeElement,
        'display',
        'block'
      );
      this.renderer2.addClass(this.waitingModalModal.nativeElement, 'show');

      this.backdropElement = this.renderer2.createElement('div');
      this.renderer2.addClass(this.backdropElement, 'modal-backdrop');
      this.renderer2.addClass(this.backdropElement, 'fade');
      this.renderer2.addClass(this.backdropElement, 'show');
      this.renderer2.appendChild(document.body, this.backdropElement);
    }
  }

  closeWaitingModal() {
    if (this.waitingModalModal) {
      this.renderer2.setStyle(
        this.waitingModalModal.nativeElement,
        'display',
        'none'
      );
      this.renderer2.removeClass(this.waitingModalModal.nativeElement, 'show');

      if (this.backdropElement) {
        this.renderer2.removeChild(document.body, this.backdropElement);
        this.backdropElement = null;
      }
    }
  }

  onMedicationSelect(event: any) {
    const medicationValues = event?.value;
    this.medications = [...new Set(this.medications)];
  }

  onComplaintSelect(event: any) {
    const complaintValues = event?.value;
    this.complaints = [...new Set(this.complaints)];
  }

  get isButtonActive(): boolean {
    return this.patientImages.some((image) => image.isSelected);
  }

  getSelectedImage(): any {
    this.selectedImage =
      this.patientImages.find((image) => image.isSelected) || null;
    return this.selectedImage;
  }

  // Submit Form Section
  generalAppearance(event: any) {
    const formArray: FormArray = this.patientInformationForm.get(
      'generalAppearances'
    ) as FormArray;

    if (event.target.checked) {
      formArray.push(new FormControl(event.target.value));
    } else {
      const index = formArray.controls.findIndex(
        (x) => x.value === event.target.value
      );
      formArray.removeAt(index);
    }
  }

  savePatientInformationForm() {
    this.patientInformationForm.value.birthDate = this.formatDate(
      this.patientInformationForm.value.birthDate
    );

    const generalAppearancesArray =
      this.patientInformationForm.value.generalAppearances || [];
    const generalAppearancesObject = {
      freckles: generalAppearancesArray.includes('freckles'),
      redHair: generalAppearancesArray.includes('redHair'),
      lightColoredEyes: generalAppearancesArray.includes('lightColoredEyes'),
    };

    this.patientInformationObject = {
      ...this.patientInformationForm.value,
      gender: Number(this.patientInformationForm.value.gender),
      generalAppearance: generalAppearancesObject,
      customerCode: this.currentUser?.customerCode,
    };
    delete this.patientInformationObject.generalAppearances;
  }

  generalComplaint(event: any) {
    const formArray: FormArray = this.lesionCharacteristicsForm.get(
      'complaints'
    ) as FormArray;

    if (event.target.checked) {
      formArray.push(new FormControl(event.target.value));
    } else {
      const index = formArray.controls.findIndex(
        (x) => x.value === event.target.value
      );
      formArray.removeAt(index);
    }
  }

  sinceCheckbox(event: any) {
    const formArray: FormArray = this.lesionCharacteristicsForm.get(
      'since'
    ) as FormArray;

    if (event.target.checked) {
      this.lesionCharacteristicsForm.get('customCongenitalValue')?.disable();
      this.lesionCharacteristicsForm.get('customCongenitalUnit')?.disable();
      formArray.push(new FormControl(event.target.value));
    } else {
      this.lesionCharacteristicsForm.get('customCongenitalValue')?.enable();
      this.lesionCharacteristicsForm.get('customCongenitalUnit')?.enable();
      const index = formArray.controls.findIndex(
        (x) => x.value === event.target.value
      );
      formArray.removeAt(index);
    }
  }

  changeCheckbox(event: any) {
    const formArray: FormArray = this.lesionCharacteristicsForm.get(
      'change'
    ) as FormArray;

    if (event.target.checked) {
      this.selectedChangeItems.push(event.target.value);

      formArray.push(new FormControl(event.target.value));
    } else {
      const index = formArray.controls.findIndex(
        (x) => x.value === event.target.value
      );
      if (index > -1) {
        formArray.removeAt(index);
      }

      const selectedIndex = this.selectedChangeItems.indexOf(
        event.target.value
      );
      if (selectedIndex > -1) {
        this.selectedChangeItems.splice(selectedIndex, 1);
      }
    }
  }

  onMedicalHistoryChange(event: Event, type: string): void {
    if (type === 'risk-factors') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedRiskFactors.push(id);
      } else {
        this.selectedRiskFactors = this.selectedRiskFactors.filter(
          (selectedId) => selectedId !== id
        );
      }
    } else if (type === 'dermatologic-diseases') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedDermatologicDiseases.push(id);
      } else {
        this.selectedDermatologicDiseases =
          this.selectedDermatologicDiseases.filter(
            (selectedId) => selectedId !== id
          );
      }
    } else if (type === 'neoplastic-diseases') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedNeoplasticDiseases.push(id);
      } else {
        this.selectedNeoplasticDiseases =
          this.selectedNeoplasticDiseases.filter(
            (selectedId) => selectedId !== id
          );
      }
    } else if (type === 'diseases-causing-immunosuppression') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedDiseasesCausingImmunosuppression.push(id);
      } else {
        this.selectedDiseasesCausingImmunosuppression =
          this.selectedDiseasesCausingImmunosuppression.filter(
            (selectedId) => selectedId !== id
          );
      }
    } else if (type === 'other-diseases') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedOtherDiseases.push(id);
      } else {
        this.selectedOtherDiseases = this.selectedOtherDiseases.filter(
          (selectedId) => selectedId !== id
        );
      }
    } else if (type === 'medications') {
      const checkbox = event.target as HTMLInputElement;
      const id = checkbox.value;
      if (checkbox.checked) {
        this.selectedMedications.push(id);
      } else {
        this.selectedMedications = this.selectedMedications.filter(
          (selectedId) => selectedId !== id
        );
      }
    }
  }

  saveAndTakePicture(e: Event) {
    e.preventDefault();

    const generalComplaintsObject = {
      itching: false,
      pain: false,
      bleeding: false,
      ulceration: false,
    };

    const changeObject = {
      hasSizeChange: false,
      hasGreaterThan6mmChange: false,
      hasBorderChange: false,
      hasColorChange: false,
    };

    const sinceObject = {
      hasCongenital: false,
      customCongenitalValue: null,
    };

    const lesionCharacteristic = {
      ...generalComplaintsObject,
      ...sinceObject,
      ...changeObject,
    };

    const form = {
      ...this.patientInformationObject,
      riskFactors: this.selectedRiskFactors,
      dermatologicDiseases: this.selectedDermatologicDiseases,
      neoplasticDiseases: this.selectedNeoplasticDiseases,
      diseaseCousingImmunosuppressions:
        this.selectedDiseasesCausingImmunosuppression,
      otherDiseases: this.selectedOtherDiseases,
      medications: this.selectedMedications,
      lesionCharacteristic: lesionCharacteristic,
    };

    this.consultationsService
      .saveAndTakeImage(form)
      .pipe(
        finalize(() => {
          this.isBtnEnabled = false;
        })
      )
      .subscribe({
        next: async (res: any) => {
          this.openWaitingModal();
          this.showSaveAndTakePicture = false;
          this.caseId = res?.patientCaseId;
          this.patientId = res?.patientId;
          this.takeImageAgainBtn = false;
          this.patientInformationForm.disable();
          this.lesionCharacteristicsForm.disable();
          this.isFormElementsEnabled = true;
          this.isSaveAndTakeDisabled = true;
          this.createPatientRecordWithQrCode();
          this.imageInterval = setInterval(() => {
            this.getPatientRecordImagesByPatientId(res.patientCaseId);
          }, 5000);

          this.toastService.showToast(
            this.translate.instant('successSaveAndTakePictureDesc'),
            'success'
          );
        },
      });
  }

  goToOtherTab(type: string) {
    if (type === 'medical-history') {
      if (this.patientInformationForm.valid) {
        const tabLinks = document.querySelectorAll('.nav-link');
        const tabPanes = document.querySelectorAll('.tab-pane');

        tabLinks.forEach((link) => link.classList.remove('active'));
        tabPanes.forEach((pane) => pane.classList.remove('show', 'active'));
        const medicalHistoryTabLink = document.querySelector(
          '[href="#medical-history"]'
        );
        const medicalHistoryTabPane =
          document.querySelector('#medical-history');

        if (medicalHistoryTabLink && medicalHistoryTabPane) {
          medicalHistoryTabLink.classList.add('active');
          medicalHistoryTabPane.classList.add('show', 'active');
        }

        this.savePatientInformationForm();
      } else {
        this.toastService.showToast(
          this.translate.instant('warningRequiredFieldsDesc'),
          'warning'
        );

        this.patientInformationForm.markAllAsTouched();
      }
    } else if (type === 'medications') {
      this.showSaveAndTakePictureButton = false;
      const tabLinks = document.querySelectorAll('.nav-link');
      const tabPanes = document.querySelectorAll('.tab-pane');

      tabLinks.forEach((link) => link.classList.remove('active'));
      tabPanes.forEach((pane) => pane.classList.remove('show', 'active'));
      const medicalHistoryTabLink = document.querySelector(
        '[href="#medications"]'
      );
      const medicalHistoryTabPane = document.querySelector('#medications');

      if (medicalHistoryTabLink && medicalHistoryTabPane) {
        medicalHistoryTabLink.classList.add('active');
        medicalHistoryTabPane.classList.add('show', 'active');
      }
    }
  }

  isSaveButtonDisable() {
    return !(
      this.patientInformationForm.valid && !this.showSaveAndTakePictureButton
    );
  }

  onDateInputChange(value: string): void {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const today = new Date();
    let formattedValue = '';

    if (sanitizedValue.length <= 2) {
      formattedValue = sanitizedValue;
    } else if (sanitizedValue.length <= 4) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2
      )}`;
    } else if (sanitizedValue.length <= 8) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2,
        4
      )}-${sanitizedValue.slice(4)}`;
    }

    const [day, month, year] = formattedValue
      .split('-')
      .map((part) => parseInt(part, 10));

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 145;
    if (
      formattedValue.startsWith('00') ||
      formattedValue.substring(3, 5) === '00' ||
      formattedValue.substring(6, 7) === '0' ||
      (day && day > 31) ||
      (month && month > 12) ||
      (year &&
        year.toString().length == 4 &&
        (year > currentYear || year < minYear))
    ) {
      this.patientInformationForm.patchValue({
        birthDate: '',
      });
      return;
    }

    const inputDate = new Date(year, month - 1, day);

    const todayFormatted = `${today.getDate().toString().padStart(2, '0')}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${today.getFullYear()}`;

    if (sanitizedValue.length === 8 && inputDate > today) {
      this.patientInformationForm.patchValue({
        birthDate: todayFormatted,
      });
    } else {
      this.patientInformationForm.patchValue({
        birthDate: formattedValue,
      });
    }
  }

  formatDate2(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  showImageFullscreen(image: any) {
    this.showImageGallery = false;
    this.fullscreenImage = image;
  }
  closeFullscreenImage() {
    this.showImageGallery = true;
    this.fullscreenImage = null;
  }

  checkNameLastnameBirthDateExists() {
    const name = this.patientInformationForm.get('name')?.value;
    const lastName = this.patientInformationForm.get('lastName')?.value;
    const birthDate = this.formatBirthDateService.formatBirthDate(
      this.patientInformationForm.get('birthDate')?.value
    );
    if (!name || !lastName || !birthDate) return;
    const params = {
      name,
      lastName,
      birthDate,
    };
    this.patientsService.getPatientByNameAndBirthDate(params).subscribe({
      next: (res) => {
        if (res?.result && res?.birthDate && res?.nameLastName) {
          this.openNameLastnameBirthDateValidationErrorModal();
        }
      },
    });
  }

  openNameLastnameBirthDateValidationErrorModal() {
    if (this.nameLastnameBirthDateValidationErrorModal) {
      this.renderer2.setStyle(
        this.nameLastnameBirthDateValidationErrorModal.nativeElement,
        'display',
        'block'
      );
      this.renderer2.addClass(
        this.nameLastnameBirthDateValidationErrorModal.nativeElement,
        'show'
      );
      this.backdropElement = this.renderer2.createElement('div');
      this.renderer2.addClass(this.backdropElement, 'modal-backdrop');
      this.renderer2.addClass(this.backdropElement, 'fade');
      this.renderer2.addClass(this.backdropElement, 'show');
      this.renderer2.appendChild(document.body, this.backdropElement);
    }
  }

  closeNameLastnameBirthDateValidationErrorModal(type: 'yes' | 'no') {
    if (type === 'no') {
      this.patientInformationForm.get('name')?.setValue('');
      this.patientInformationForm.get('lastName')?.setValue('');
      this.patientInformationForm.get('birthDate')?.setValue('');
    }

    if (this.nameLastnameBirthDateValidationErrorModal) {
      this.renderer2.setStyle(
        this.nameLastnameBirthDateValidationErrorModal.nativeElement,
        'display',
        'none'
      );
      this.renderer2.removeClass(
        this.nameLastnameBirthDateValidationErrorModal.nativeElement,
        'show'
      );
      if (this.backdropElement) {
        this.renderer2.removeChild(document.body, this.backdropElement);
        this.backdropElement = null;
      }
    }
  }

  preventForbiddenCharacters(
    event: Event,
    type: 'number' | 'text' | 'email' | 'tel'
  ): void {
    this.inputValidationService.preventForbiddenCharacters(event, type);
  }
}
